import { useState } from 'react'
import {
  SITE_NOTICE,
  SITE_NOTICE_DISMISS_KEY,
  isSiteNoticeActive,
  type SiteNoticeType,
} from '../config/site'

const ICON: Record<SiteNoticeType, string> = {
  info: 'ℹ️',
  warning: '⚠️',
  maintenance: '🛠️',
}

/** Dismissible (unpinned) notices use `status`; pinned/maintenance use `alert`. */
function roleFor(type: SiteNoticeType, pinned: boolean): 'status' | 'alert' {
  return pinned || type === 'maintenance' ? 'alert' : 'status'
}

function dismissKey(id: string): string {
  return `${SITE_NOTICE_DISMISS_KEY}:${id}`
}

/** Read dismissed-state from sessionStorage (guarded for non-browser/test env). */
function wasDismissed(id: string): boolean {
  try {
    return sessionStorage.getItem(dismissKey(id)) === '1'
  } catch {
    return false
  }
}

/**
 * Site-wide notice banner, driven by the build-time {@link SITE_NOTICE} config.
 * Renders nothing when the notice is disabled/empty.
 *
 * - **Pinned** notices (e.g. maintenance) are sticky and have **no close
 *   button** — they stay until the config clears them.
 * - **Unpinned** notices are **dismissible**; the dismissal is remembered for
 *   the session, keyed by the notice `id` (a new notice id re-shows).
 */
export function SiteNotice() {
  const notice = SITE_NOTICE
  const [dismissed, setDismissed] = useState(() =>
    notice.pinned ? false : wasDismissed(notice.id),
  )

  if (!isSiteNoticeActive(notice)) return null
  if (!notice.pinned && dismissed) return null

  const dismiss = () => {
    try {
      sessionStorage.setItem(dismissKey(notice.id), '1')
    } catch {
      /* sessionStorage unavailable — dismissal is best-effort for the session */
    }
    setDismissed(true)
  }

  return (
    <div
      className={`site-notice site-notice--${notice.type}${
        notice.pinned ? ' site-notice--pinned' : ''
      }`}
      role={roleFor(notice.type, notice.pinned)}
      data-notice-id={notice.id}
    >
      <div className="container site-notice__inner">
        <span className="site-notice__icon" aria-hidden="true">
          {ICON[notice.type]}
        </span>
        <p className="site-notice__text">
          {notice.title ? <strong className="site-notice__title">{notice.title}: </strong> : null}
          {notice.message}
          {notice.link ? (
            <>
              {' '}
              <a className="site-notice__link" href={notice.link.href}>
                {notice.link.label}
              </a>
            </>
          ) : null}
        </p>
        {notice.pinned ? null : (
          <button
            type="button"
            className="site-notice__dismiss"
            aria-label="Dismiss notice"
            onClick={dismiss}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  )
}

export default SiteNotice
